import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  useDeleteSchedule,
  useOrganizationSettings,
  useSaveSchedule,
  useSchedules,
} from '../api/hooks'
import { TimeZoneSetting } from './TimeZoneSetting'
import { useAuth } from '../auth/AuthContext'
import type { Schedule } from '../api/types'
import { formatDateTime, localZoneLabel } from './formatTime'

/**
 * Recurring runs, on the Run Console (§9, §12 Phase 7).
 *
 * Not a tenth screen — a schedule is a standing instruction to do exactly what the
 * button above it does, and it belongs next to it. Admin only, matching the API.
 */

const SELECTORS = [
  { value: 'PriorMonth', label: 'Prior month', hint: 'The period that ended in the month before the run fires. What a monthly close usually wants.' },
  { value: 'Current', label: 'Current', hint: 'The period containing the fire date.' },
] as const

/*
  Two zones on one screen, deliberately.

  The cron expression is read in the ORGANIZATION's zone -- the books close at three in
  the morning where they are kept, whatever the reader's laptop is set to. Next and last
  are rendered in the READER's zone, because those are instants, and an instant is best
  read where you are standing.

  So both are labelled, every time. `0 3 * * *` in a London organization, read from New
  York in summer, shows a next fire of 22:00 the previous day -- correct, and inexplicable
  without both labels.
*/

export function SchedulePanel() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const { data: schedules = [], isLoading } = useSchedules(isAdmin)
  const { data: settings } = useOrganizationSettings()

  // Falls back to the zone the schedules themselves report, so the column heading is
  // never blank on the first render.
  const zoneLabel = settings?.timeZoneId ?? schedules[0]?.timeZoneId ?? 'UTC'
  const save = useSaveSchedule()
  const remove = useDeleteSchedule()

  const [editing, setEditing] = useState<Schedule | 'new' | null>(null)
  const [name, setName] = useState('')
  const [selector, setSelector] = useState<string>('PriorMonth')
  const [cron, setCron] = useState('0 3 3 * *')
  const [enabled, setEnabled] = useState(true)
  const [isPreview, setIsPreview] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isAdmin) {
    return null
  }

  function open(schedule: Schedule | 'new') {
    setError(null)
    setEditing(schedule)

    if (schedule === 'new') {
      setName('')
      setSelector('PriorMonth')
      setCron('0 3 3 * *')
      setEnabled(true)
      setIsPreview(false)
      return
    }

    setName(schedule.name)
    setSelector(schedule.periodSelector)
    setCron(schedule.cronExpression)
    setEnabled(schedule.isEnabled)
    setIsPreview(schedule.isPreview)
  }

  function submit() {
    setError(null)

    save.mutate(
      {
        scheduleId: editing === 'new' || editing === null ? undefined : editing.scheduleId,
        name,
        periodSelector: selector,
        cron,
        enabled,
        isPreview,
      },
      {
        onSuccess: () => setEditing(null),
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : 'The schedule could not be saved.'),
      },
    )
  }

  return (
    <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
      <div
        className="flex items-center gap-3 px-4 py-2"
        style={{ background: 'var(--color-panel-alt)' }}
      >
        <h2 className="col-head">schedules</h2>
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Cron expressions are read in {zoneLabel}; next and last are shown in your time
          ({localZoneLabel()}). The job checks every 15 minutes, so a run starts at or
          shortly after its scheduled time.
        </span>
        <button className="btn btn-sm ml-auto" onClick={() => open('new')}>
          Add schedule
        </button>
      </div>

      <TimeZoneSetting />

      {isLoading ? (
        <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-faint)' }}>
          Loading…
        </p>
      ) : schedules.length === 0 ? (
        <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-faint)' }}>
          No schedules. Runs happen only when someone presses the button.
        </p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
              <th className="col-head px-4 py-1.5">Name</th>
              <th className="col-head px-4 py-1.5">Period</th>
              <th className="col-head px-4 py-1.5">Cron ({zoneLabel})</th>
              <th className="col-head px-4 py-1.5">Next ({localZoneLabel()})</th>
              <th className="col-head px-4 py-1.5">Last ({localZoneLabel()})</th>
              <th className="col-head px-4 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => (
              <tr key={s.scheduleId} className="border-b" style={{ borderColor: 'var(--color-rule-soft)' }}>
                <td className="px-4 py-1.5 text-[12px]">
                  {s.name}
                  {!s.isEnabled && (
                    <span
                      className="badge ml-1.5"
                      style={{ background: 'var(--color-panel-alt)', color: 'var(--color-muted)' }}
                    >
                      disabled
                    </span>
                  )}
                  {s.isPreview && (
                    <span
                      className="badge ml-1.5"
                      style={{ background: 'var(--color-accent-wash)', color: 'var(--color-accent)' }}
                      title="Reports the numbers without superseding the period's results."
                    >
                      preview
                    </span>
                  )}
                </td>
                <td className="ident px-4 py-1.5">{s.periodSelector}</td>
                <td className="ident px-4 py-1.5">{s.cronExpression}</td>
                <td className="ident px-4 py-1.5" style={{ color: 'var(--color-muted)' }}>
                  {formatDateTime(s.nextFireUtc)}
                </td>
                <td className="px-4 py-1.5 text-[12px]">
                  <span className="ident" style={{ color: 'var(--color-muted)' }}>
                    {formatDateTime(s.lastFiredUtc)}
                  </span>
                  {s.lastStatus === 'Failed' && (
                    <span
                      className="badge ml-1.5"
                      style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
                      title="The last time this came due, no run could be started. Check the alert."
                    >
                      did not run
                    </span>
                  )}
                </td>
                <td className="px-4 py-1.5 text-right">
                  <button
                    className="text-[12px] underline"
                    style={{ color: 'var(--color-accent)' }}
                    onClick={() => open(s)}
                  >
                    Edit
                  </button>
                  <button
                    className="ml-3 text-[12px] underline"
                    style={{ color: 'var(--color-error)' }}
                    onClick={() => remove.mutate(s.scheduleId)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* A schedule that cannot be parsed will never fire. Saying so is the whole
          reason the API computes it. */}
      {schedules
        .filter((s) => s.configurationError)
        .map((s) => (
          <p
            key={s.scheduleId}
            className="px-4 py-1.5 text-[12px]"
            style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
            role="alert"
          >
            <strong>{s.name}</strong> will never fire: {s.configurationError}
          </p>
        ))}

      {/*
          Daylight saving. A warning rather than an error: the schedule is legal and the
          behaviour on the night is defensible, but an admin who picks 01:30 has chosen
          one of the two hours a year that behave oddly and has no other way to find out.
      */}
      {schedules
        .filter((s) => s.daylightSavingWarning)
        .map((s) => (
          <p
            key={s.scheduleId}
            className="px-4 py-1.5 text-[12px]"
            style={{ background: 'var(--color-warn-wash)', color: 'var(--color-warn)' }}
          >
            <strong>{s.name}</strong> ({s.cronExpression}): {s.daylightSavingWarning}
          </p>
        ))}

      {editing !== null && (
        <div
          className="flex flex-wrap items-end gap-3 border-t px-4 py-3"
          style={{ background: 'var(--color-panel-alt)', borderColor: 'var(--color-rule-soft)' }}
        >
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="col-head">name</span>
            <input
              className="input w-56"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Monthly close"
            />
          </label>

          <label className="flex flex-col gap-1 text-[12px]">
            <span className="col-head">period</span>
            <select
              className="input w-44"
              value={SELECTORS.some((s) => s.value === selector) ? selector : 'Code'}
              onChange={(e) =>
                setSelector(e.target.value === 'Code' ? 'Code:' : e.target.value)
              }
              title={SELECTORS.find((s) => s.value === selector)?.hint}
            >
              {SELECTORS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
              <option value="Code">A specific period…</option>
            </select>
          </label>

          {selector.startsWith('Code') && (
            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">period code</span>
              <input
                className="input input-mono w-32"
                value={selector.slice('Code:'.length)}
                onChange={(e) => setSelector(`Code:${e.target.value}`)}
                placeholder="2026-07"
              />
            </label>
          )}

          <label className="flex flex-col gap-1 text-[12px]">
            <span className="col-head">cron (UTC)</span>
            <input
              className="input input-mono w-40"
              value={cron}
              onChange={(e) => setCron(e.target.value)}
              placeholder="0 3 3 * *"
            />
          </label>

          <label className="flex items-center gap-1.5 pb-1.5 text-[12px]">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Enabled
          </label>

          <label className="flex items-center gap-1.5 pb-1.5 text-[12px]">
            <input
              type="checkbox"
              checked={isPreview}
              onChange={(e) => setIsPreview(e.target.checked)}
            />
            Preview only
          </label>

          <button className="btn btn-primary btn-sm" onClick={submit} disabled={save.isPending}>
            Save
          </button>
          <button className="btn btn-sm" onClick={() => setEditing(null)}>
            Cancel
          </button>

          {error && (
            <span className="text-[12px]" style={{ color: 'var(--color-error)' }}>
              {error}
            </span>
          )}
        </div>
      )}
    </section>
  )
}
